import CustomComponent from '../CustomComponent';
import SelectorEngine from '../../utils/dom/selector-engine';
import Request from '../../utils/request';

/**
 * YouTubeShortCarousel Web Component
 * Pass block-id to load shorts from the block API. One call to block returns videos (same shape as playlist).
 *
 * Usage (block-id only; no child elements):
 * <lta-youtube-short-carousel id="my-carousel" block-id="youtube-shorts-demo" slides-xl="6" slides-md="3" slides-sm="1"></lta-youtube-short-carousel>
 *
 * Props:
 * - id: unique identifier for the carousel
 * - block-id: block identifier – one call to block API returns videos array (same as playlist)
 * - block-endpoint: optional; block API URL (default /api/youtube/block)
 * - cache-duration: optional; passed to the block API (e.g. cache duration in minutes)
 * - slides-xl, slides-md, slides-sm: slides per view (defaults: 4, 4, 2)
 */
export class YouTubeShortCarouselComponent extends CustomComponent {
  static key = 'lta-youtube-short-carousel';

  constructor() {
    super();
    this.state = {
      currentVideoIndex: 0,
      videos: []
    };
  }

  static get observedAttributes() {
    return ['id', 'block-id', 'block-endpoint', 'cache-duration', 'slides-xl', 'slides-md', 'slides-sm'];
  }

  style() {
    // Styles are in src/styles/7-components/youtube-short-carousel/youtube-short-carousel.scss
    return '';
  }

  _escapeHtml(str) {
    if (str == null || typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  _slidesFromVideos(videos) {
    return videos.map((v) => {
      const code = this._escapeHtml(v.videoCode || v.videoId || '');
      const title = this._escapeHtml(v.title || 'YouTube video player');
      const thumb = this._escapeHtml(v.thumbnail || `https://i.ytimg.com/vi/${v.videoCode || v.videoId}/maxresdefault.jpg`);
      return `<div class="swiper-slide" role="listitem"><lta-youtube-shorts video-code="${code}" title="${title}" thumbnail="${thumb}"></lta-youtube-shorts></div>`;
    }).join('');
  }

  render(childrenHTML) {
    const { id, blockId, blockEndpoint, cacheDuration, slidesXl = '4', slidesMd = '4', slidesSm = '2' } = this.props;
    const { videos = [] } = this.state;
    const childMarkup = this._childrenHTML || childrenHTML || '';

    const hasBlockId = blockId != null && blockId !== '';
    const errorMsg = this.state.error || '';
    if (hasBlockId && videos.length === 0) {
      const dataBlockEndpoint = blockEndpoint != null && blockEndpoint !== '' ? ` data-block-endpoint="${this._escapeHtml(blockEndpoint)}"` : '';
      return `
        <div class="lta-youtube-short-carousel" data-block-id="${this._escapeHtml(blockId)}"${dataBlockEndpoint}>
          ${errorMsg
            ? `<div class="lta-youtube-short-carousel__error u-text-center u-py-4" role="alert" aria-live="assertive"><p class="u-text-error">${this._escapeHtml(errorMsg)}</p></div>`
            : '<div class="lta-youtube-short-carousel__loading u-text-center u-py-4" role="status" aria-live="polite"><p>Loading shorts...</p></div>'}
          ${this._modalHtml()}
        </div>`;
    }

    if (hasBlockId && videos.length > 0) {
      const slides = this._slidesFromVideos(videos);
      return this._carouselAndModalHtml(id || Date.now(), blockId, blockEndpoint, cacheDuration, slidesXl, slidesMd, slidesSm, slides);
    }

    if (!childMarkup.trim()) {
      return '<div class="lta-youtube-short-carousel__error" role="alert">Provide block-id or lta-youtube-shorts children.</div>';
    }

    const wrappedChildren = this._wrapChildrenInSlides(childMarkup);
    return this._carouselAndModalHtml(id || Date.now(), blockId, blockEndpoint, cacheDuration, slidesXl, slidesMd, slidesSm, wrappedChildren);
  }

  _modalHtml() {
    return `
      <div class="lta-youtube-short-carousel__modal" role="dialog" aria-modal="true" aria-labelledby="youtube-short-modal-title" aria-describedby="youtube-short-modal-live" aria-hidden="true">
        <button type="button" class="lta-youtube-short-carousel__modal-close lta-btn lta-btn--default lta-btn--link lta-btn--icon lta-btn--icon-after js-exit-accreditation-renewal" aria-label="Exit video and return" tabindex="0">
          Exit
          <svg class="lta-icon js-lta-text-input--btn-icon u-ml-0" width="1.5em" height="1.5em" style="color:currentColor;" fill="currentColor" aria-hidden="true">
            <use xlink:href="/static/images/icons/lta-icons.svg#closev2"></use>
          </svg>
        </button>
        <div class="lta-youtube-short-carousel__modal-backdrop" aria-hidden="true"></div>
        <div class="lta-youtube-short-carousel__modal-container" tabindex="-1">
          <div class="lta-youtube-short-carousel__modal-top-bar" role="toolbar" aria-label="Video controls" aria-hidden="true">
            <button type="button" class="lta-youtube-short-carousel__modal-top-bar-btn lta-youtube-short-carousel__modal-top-bar-pause" aria-label="Pause video" tabindex="0">
              <svg class="lta-youtube-short-carousel__modal-top-bar-icon lta-youtube-short-carousel__modal-top-bar-icon--pause" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/></svg>
              <svg class="lta-youtube-short-carousel__modal-top-bar-icon lta-youtube-short-carousel__modal-top-bar-icon--play u-hidden" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
            </button>
            <button type="button" class="lta-youtube-short-carousel__modal-top-bar-btn lta-youtube-short-carousel__modal-top-bar-close" aria-label="Close video" tabindex="0">
              <svg class="lta-icon" width="1.5em" height="1.5em" fill="currentColor" aria-hidden="true"><use xlink:href="/static/images/icons/lta-icons.svg#closev2"></use></svg>
            </button>
          </div>
          <h2 id="youtube-short-modal-title" class="sr-only">YouTube Short video player</h2>
          <div id="youtube-short-modal-live" class="sr-only" aria-live="polite" aria-atomic="true"></div>
          <div class="lta-youtube-short-carousel__modal-content">
            <div class="lta-youtube-short-carousel__modal-body">
              <div class="lta-youtube-short-carousel__modal-video-wrapper" id="modal-video-wrapper" role="group" aria-label="Video player"></div>
            </div>
            <div class="lta-youtube-short-carousel__modal-nav-bar" role="group" aria-label="Video navigation">
              <button type="button" class="lta-youtube-short-carousel__modal-nav lta-youtube-short-carousel__modal-nav--prev" aria-label="Previous video">
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
                  <path d="M2.7999 6L7.3999 10.6L5.9999 12L-9.77516e-05 6L5.9999 0L7.3999 1.4L2.7999 6Z" fill="currentColor"/>
                </svg>
              </button>
              <button type="button" class="lta-youtube-short-carousel__modal-nav lta-youtube-short-carousel__modal-nav--next" aria-label="Next video">
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
                  <path d="M4.6 6L0 1.4L1.4 0L7.4 6L1.4 12L0 10.6L4.6 6Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>`;
  }

  _carouselAndModalHtml(carouselId, blockId, blockEndpoint, cacheDuration, slidesXl, slidesMd, slidesSm, slidesContent) {
    const dataBlockId = blockId != null && blockId !== '' ? ` data-block-id="${this._escapeHtml(blockId)}"` : '';
    const dataBlockEndpoint = blockEndpoint != null && blockEndpoint !== '' ? ` data-block-endpoint="${this._escapeHtml(blockEndpoint)}"` : '';
    const dataCacheDuration = cacheDuration != null && cacheDuration !== '' ? ` data-cache-duration="${cacheDuration}"` : '';

    return `
      <div class="lta-carouselv2" id="youtube-short-carousel-${carouselId}"${dataBlockId}${dataBlockEndpoint}${dataCacheDuration} role="region" aria-label="YouTube Shorts carousel"
        data-slides-per-view-xl="${slidesXl}"
        data-slides-per-view-md="${slidesMd}"
        data-slides-per-view="${slidesSm}">
        <div class="swiper">
          <div class="swiper-wrapper" role="list">
            ${slidesContent}
          </div>
          <div class="lta-carouselv2__controls">
            <button type="button" class="lta-carouselv2__prev u-mr-2" aria-label="Previous video">
              <svg class="lta-icon js-lta-text-input--btn-icon" width="2rem" height="2rem" style="color:currentColor;" fill="currentColor" aria-hidden="true">
                <use xlink:href="/static/images/icons/lta-icons.svg#arrow-left-circle"></use>
              </svg>
            </button>
            <div class="lta-carouselv2__pagination u-hstack u-gap-1" role="tablist" aria-label="Slide pagination"></div>
            <button type="button" class="lta-carouselv2__next u-ml-2" aria-label="Next video">
              <svg class="lta-icon js-lta-text-input--btn-icon" width="2rem" height="2rem" style="color:currentColor;" fill="currentColor" aria-hidden="true">
                <use xlink:href="/static/images/icons/lta-icons.svg#arrow-right-circle"></use>
              </svg>
            </button>
          </div>
          <span class="swiper-notification" aria-live="assertive" aria-atomic="true"></span>
        </div>
      </div>
      
      <!-- Modal -->
      <div class="lta-youtube-short-carousel__modal" role="dialog" aria-modal="true" aria-labelledby="youtube-short-modal-title" aria-describedby="youtube-short-modal-live" aria-hidden="true">
        <button type="button" class="lta-youtube-short-carousel__modal-close lta-btn lta-btn--default lta-btn--link lta-btn--icon lta-btn--icon-after js-exit-accreditation-renewal" aria-label="Exit video and return" tabindex="0">
          Exit
          <svg class="lta-icon js-lta-text-input--btn-icon u-ml-0" width="1.5em" height="1.5em" style="color:currentColor;" fill="currentColor" aria-hidden="true">
            <use xlink:href="/static/images/icons/lta-icons.svg#closev2"></use>
          </svg>
        </button>
        <div class="lta-youtube-short-carousel__modal-backdrop" aria-hidden="true"></div>
        <div class="lta-youtube-short-carousel__modal-container" tabindex="-1">
          <div id="youtube-short-modal-live" class="sr-only" aria-live="polite" aria-atomic="true"></div>
          <div class="lta-youtube-short-carousel__modal-content">
            <div class="lta-youtube-short-carousel__modal-top-bar" role="toolbar" aria-label="Video controls" aria-hidden="true">
              <button type="button" class="lta-youtube-short-carousel__modal-top-bar-btn lta-youtube-short-carousel__modal-top-bar-pause" aria-label="Pause video" tabindex="0">
                <svg class="lta-youtube-short-carousel__modal-top-bar-icon lta-youtube-short-carousel__modal-top-bar-icon--pause" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M6 19H10V5H6V19ZM14 5V19H18V5H14Z" fill="white"/>
                </svg>
                <svg class="lta-youtube-short-carousel__modal-top-bar-icon lta-youtube-short-carousel__modal-top-bar-icon--play u-hidden" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
              </button>
              <button type="button" class="lta-youtube-short-carousel__modal-top-bar-btn lta-youtube-short-carousel__modal-top-bar-close" aria-label="Close video" tabindex="0">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="18.0104" y="15.8887" width="3" height="14" transform="rotate(135 18.0104 15.8887)" fill="white"/>
                  <rect x="15.889" y="5.98926" width="3" height="14" transform="rotate(45 15.889 5.98926)" fill="white"/>
                </svg>
              </button>
            </div>
            <div class="lta-youtube-short-carousel__modal-body">
              <div class="lta-youtube-short-carousel__modal-video-wrapper" id="modal-video-wrapper" role="group" aria-label="Video player">
                <!-- Video iframe inserted here -->
              </div>
            </div>
            <div class="lta-youtube-short-carousel__modal-nav-bar" role="group" aria-label="Video navigation">
              <button type="button" class="lta-youtube-short-carousel__modal-nav lta-youtube-short-carousel__modal-nav--prev" aria-label="Previous video">
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
                  <path d="M2.7999 6L7.3999 10.6L5.9999 12L-9.77516e-05 6L5.9999 0L7.3999 1.4L2.7999 6Z" fill="currentColor"/>
                </svg>
              </button>
              <button type="button" class="lta-youtube-short-carousel__modal-nav lta-youtube-short-carousel__modal-nav--next" aria-label="Next video">
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
                  <path d="M4.6 6L0 1.4L1.4 0L7.4 6L1.4 12L0 10.6L4.6 6Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  _wrapChildrenInSlides(childrenHTML) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${childrenHTML}</div>`, 'text/html');
    const shortElements = doc.querySelectorAll('lta-youtube-shorts');
    return Array.from(shortElements).map((short) =>
      `<div class="swiper-slide" role="listitem">${short.outerHTML}</div>`
    ).join('');
  }

  onLoad() {
    const { blockId } = this.props;
    if (blockId && (!this.state.videos || this.state.videos.length === 0)) {
      this._fetchShortsByBlockId();
    } else if (!blockId) {
      this._parseVideos();
    }
  }

  async _fetchShortsByBlockId() {
    const { blockId, blockEndpoint, cacheDuration } = this.props;
    if (!blockId) return;
    const endpoint = blockEndpoint != null && String(blockEndpoint).trim() !== '' ? blockEndpoint.trim() : '/api/youtube/block';
    try {
      const query = { blockId };
      if (cacheDuration != null && cacheDuration !== '') {
        query.cacheDuration = cacheDuration;
      }
      const blockRes = await Request.get(endpoint, query);
      if (blockRes?.errors?.errorCode !== '00' || blockRes?.response?.type !== 'shorts') {
        this.setState({ videos: [], error: blockRes?.error?.message || 'Failed to load shorts' });
        return;
      }
      const list = blockRes.response?.videos || [];
      const videos = list.map((v) => ({
        videoCode: v.videoId,
        videoId: v.videoId,
        title: v.title || 'YouTube video player',
        thumbnail: v.thumbnail || `https://i.ytimg.com/vi/${v.videoId}/maxresdefault.jpg`,
        width: '315',
        height: '560'
      }));
      this.setState({ videos });
    } catch (err) {
      this.setState({ videos: [], error: err?.message || 'Failed to load shorts' });
    }
  }

  _parseVideos() {
    const shorts = this.querySelectorAll('lta-youtube-shorts');
    const videos = Array.from(shorts).map(short => ({
      videoCode: short.getAttribute('video-code'),
      title: short.getAttribute('title') || 'YouTube video player',
      thumbnail: short.getAttribute('thumbnail') || '',
      width: short.getAttribute('width') || '315',
      height: short.getAttribute('height') || '560'
    }));
    this.setState({ videos });
  }

  afterRender() {
    const carouselEl = this.querySelector('.lta-carouselv2');
    if (carouselEl) this._initializeCarousel(carouselEl);

    this.addEventListener('youtube-short-play', this._handlePlayEvent.bind(this));

    // Use delegation so Exit/close works after re-renders and when clicking icon or text inside the button
    this._boundCloseModal = this._closeModal.bind(this);
    this._boundModalCloseClick = this._handleModalCloseClick.bind(this);
    this.addEventListener('click', this._boundModalCloseClick);

    const backdrop = this.querySelector('.lta-youtube-short-carousel__modal-backdrop');
    if (backdrop) backdrop.addEventListener('click', this._boundCloseModal);

    const prevButton = this.querySelector('.lta-youtube-short-carousel__modal-nav--prev');
    const nextButton = this.querySelector('.lta-youtube-short-carousel__modal-nav--next');
    if (prevButton) prevButton.addEventListener('click', this._prevVideo.bind(this));
    if (nextButton) nextButton.addEventListener('click', this._nextVideo.bind(this));

    document.addEventListener('keydown', this._handleKeyPress.bind(this));
  }

  onUnload() {
    this.removeEventListener('youtube-short-play', this._handlePlayEvent);
    if (this._boundModalCloseClick) this.removeEventListener('click', this._boundModalCloseClick);
    document.removeEventListener('keydown', this._handleKeyPress);
  }

  _handleModalCloseClick(event) {
    const target = event.target.closest('button');
    if (!target) return;
    if (target.classList.contains('lta-youtube-short-carousel__modal-close') || target.classList.contains('lta-youtube-short-carousel__modal-top-bar-close')) {
      event.preventDefault();
      this._closeModal();
      return;
    }
    if (target.classList.contains('lta-youtube-short-carousel__modal-top-bar-pause')) {
      event.preventDefault();
      this._togglePause();
      return;
    }
  }

  _handlePlayEvent(event) {
    const { detail } = event;
    const { videos } = this.state;
    const index = videos.findIndex((v) => v.videoCode === detail.videoCode);
    if (index !== -1) {
      this._state.currentVideoIndex = index;
      this._openModal();
      this._loadVideoInModal(videos[index], true);
    }
  }

  _openModal() {
    this.querySelectorAll('.lta-youtube-shorts__play-button').forEach((btn) => btn.classList.remove('is-playing'));
    const modal = this.querySelector('.lta-youtube-short-carousel__modal');
    if (modal) {
      this._focusBeforeModal = document.activeElement;
      modal.setAttribute('aria-hidden', 'false');
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this._resetPauseButton();
      const topBar = this.querySelector('.lta-youtube-short-carousel__modal-top-bar');
      if (topBar) topBar.setAttribute('aria-hidden', 'false');
      this._announceModalVideo();
      requestAnimationFrame(() => {
        const focusables = this._getModalFocusables();
        const focusTarget = focusables.length > 0 ? focusables[0] : null;
        if (focusTarget) focusTarget.focus();
      });
    }
  }

  _closeModal() {
    this._stopModalVideo();
    const modal = this.querySelector('.lta-youtube-short-carousel__modal');
    if (modal) {
      modal.setAttribute('aria-hidden', 'true');
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
      const topBar = this.querySelector('.lta-youtube-short-carousel__modal-top-bar');
      if (topBar) topBar.setAttribute('aria-hidden', 'true');
      if (this._focusBeforeModal && typeof this._focusBeforeModal.focus === 'function') {
        this._focusBeforeModal.focus();
      }
    }
  }

  _stopModalVideo() {
    const container = this.querySelector('#modal-video-wrapper');
    if (container) container.innerHTML = '';
    const titleEl = this.querySelector('#modal-video-title');
    if (titleEl) titleEl.textContent = '';
  }

  _getModalVideoIframe() {
    return this.querySelector('#modal-video-iframe');
  }

  _sendVideoCommand(func) {
    const iframe = this._getModalVideoIframe();
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func }), 'https://www.youtube.com');
  }

  _resetPauseButton() {
    this._modalVideoPaused = false;
    const btn = this.querySelector('.lta-youtube-short-carousel__modal-top-bar-pause');
    if (!btn) return;
    const pauseIcon = btn.querySelector('.lta-youtube-short-carousel__modal-top-bar-icon--pause');
    const playIcon = btn.querySelector('.lta-youtube-short-carousel__modal-top-bar-icon--play');
    btn.setAttribute('aria-label', 'Pause video');
    if (pauseIcon) pauseIcon.classList.remove('u-hidden');
    if (playIcon) playIcon.classList.add('u-hidden');
  }

  _togglePause() {
    const iframe = this._getModalVideoIframe();
    if (!iframe) return;
    this._modalVideoPaused = !this._modalVideoPaused;
    this._sendVideoCommand(this._modalVideoPaused ? 'pauseVideo' : 'playVideo');
    const btn = this.querySelector('.lta-youtube-short-carousel__modal-top-bar-pause');
    if (!btn) return;
    const pauseIcon = btn.querySelector('.lta-youtube-short-carousel__modal-top-bar-icon--pause');
    const playIcon = btn.querySelector('.lta-youtube-short-carousel__modal-top-bar-icon--play');
    btn.setAttribute('aria-label', this._modalVideoPaused ? 'Play video' : 'Pause video');
    if (pauseIcon) pauseIcon.classList.toggle('u-hidden', this._modalVideoPaused);
    if (playIcon) playIcon.classList.toggle('u-hidden', !this._modalVideoPaused);
  }

  _handleKeyPress(event) {
    const modal = this.querySelector('.lta-youtube-short-carousel__modal');
    if (!modal || !modal.classList.contains('is-open')) return;
    
    switch (event.key) {
      case 'Escape':
        this._closeModal();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this._prevVideo();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this._nextVideo();
        break;
      case 'Tab':
        this._trapModalFocus(event);
        break;
    }
  }

  _trapModalFocus(event) {
    const focusables = this._getModalFocusables();
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  _prevVideo() {
    const { currentVideoIndex, videos } = this.state;
    const newIndex = currentVideoIndex > 0 ? currentVideoIndex - 1 : videos.length - 1;
    this._state.currentVideoIndex = newIndex;
    this._loadVideoInModal(videos[newIndex], true);
  }

  _nextVideo() {
    const { currentVideoIndex, videos } = this.state;
    const newIndex = currentVideoIndex < videos.length - 1 ? currentVideoIndex + 1 : 0;
    this._state.currentVideoIndex = newIndex;
    this._loadVideoInModal(videos[newIndex], true);
  }

  _announceModalVideo() {
    const liveEl = this.querySelector('#youtube-short-modal-live');
    if (!liveEl) return;
    const { currentVideoIndex, videos } = this.state;
    const total = videos.length;
    const current = videos[currentVideoIndex];
    const title = current ? (current.title || 'YouTube video player') : '';
    const oneBased = total ? currentVideoIndex + 1 : 0;
    liveEl.textContent = total ? `Video ${oneBased} of ${total}: ${title}` : '';
  }

  _getModalFocusables() {
    const modal = this.querySelector('.lta-youtube-short-carousel__modal');
    if (!modal) return [];
    const selector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const list = SelectorEngine.find(selector, modal).filter(el => {
      return el.offsetParent !== null && !el.hasAttribute('aria-hidden');
    });
    return this._modalFocusablesInOrder(list);
  }

  /**
   * Returns focusables in desired tab order: play/pause first (mobile top bar), then close, then Exit, then prev/next.
   */
  _modalFocusablesInOrder(focusables) {
    if (focusables.length <= 1) return focusables;
    const pause = this.querySelector('.lta-youtube-short-carousel__modal-top-bar-pause');
    const topBarClose = this.querySelector('.lta-youtube-short-carousel__modal-top-bar-close');
    const exit = this.querySelector('.lta-youtube-short-carousel__modal-close');
    const prev = this.querySelector('.lta-youtube-short-carousel__modal-nav--prev');
    const next = this.querySelector('.lta-youtube-short-carousel__modal-nav--next');
    const preferred = [pause, topBarClose, exit, prev, next].filter(Boolean);
    const set = new Set(focusables);
    const ordered = preferred.filter((el) => set.has(el));
    const rest = focusables.filter((el) => !ordered.includes(el));
    return [...ordered, ...rest];
  }

  _loadVideoInModal(video, autoplay = false) {
    const container = this.querySelector('#modal-video-wrapper');
    if (!container) return;

    const { videoCode, title, width, height } = video;

    const params = new URLSearchParams({
      loop: '1',
      playlist: videoCode,
      controls: '0', // Hide YouTube controls – we use our own title/info
      modestbranding: '1',
      playsinline: '1',
      enablejsapi: '1',
      rel: '0',
      iv_load_policy: '3',
      cc_load_policy: '0'
    });
    if (autoplay) params.append('autoplay', '1');

    const iframeUrl = `https://www.youtube.com/embed/${videoCode}?${params.toString()}`;
    const safeTitle = title.replace(/"/g, '&quot;');
    container.innerHTML = `
      <iframe
        id="modal-video-iframe"
        src="${iframeUrl}"
        title="${safeTitle}"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowfullscreen
        style="width: 100%; aspect-ratio: ${width} / ${height}; border-radius: 8px;">
      </iframe>
    `;
    this._resetPauseButton();
    this._announceModalVideo();
  }

  _initializeCarousel(carouselEl) {
    // Initialize carousel using existing CarouselV2 logic
    import('../../components/CarouselV2').then(module => {
      const CarouselV2 = module.default;
      const carousel = CarouselV2.getOrCreateInstance(carouselEl);
      carousel?.update();
    });
  }
}

export default YouTubeShortCarouselComponent;

